import React, { useState, useEffect } from 'react';
import { COLORS, SERVICES, TRANSLATIONS, PLATFORM_FEE_PCT, EXPERT_EARNING_PCT } from '../data/constants';
import { supabase } from '../supabase';
import { GharPeLogoWhite } from '../components/Logo';
import { Badge, BottomNav, LangToggle, LoadingSpinner, EmptyState } from '../components/Shared';

const C = COLORS;

// ── EXPERT DETAIL MODAL ───────────────────────────────────────────────────────
function ExpertDetailModal({ expert, onClose, onStatusUpdate }) {
  const [note, setNote]       = useState('');
  const [updating, setUpdate] = useState(false);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    if (!expert) return;
    supabase.from('bookings').select('*')
      .eq('pro_phone', expert.phone)
      .order('created_at', { ascending: false })
      .limit(10)
      .then(({ data }) => setBookings(data || []));
  }, [expert?.phone]);

  const updateStatus = async (status) => {
    setUpdate(true);
    const { error } = await supabase.from('professionals')
      .update({ verification_status: status, admin_note: note, updated_at: new Date().toISOString() })
      .eq('phone', expert.phone);
    if (error) { alert('Update failed: ' + error.message); }
    else { onStatusUpdate(expert.phone, status, note); onClose(); }
    setUpdate(false);
  };

  if (!expert) return null;

  const statusColors = {
    approved:     { bg: '#D1FAE5', color: '#065F46' },
    pending:      { bg: '#FEF3C7', color: '#92400E' },
    under_review: { bg: '#FEF3C7', color: '#92400E' },
    rejected:     { bg: '#FEE2E2', color: '#DC2626' },
    more_docs:    { bg: '#EFF6FF', color: '#1D4ED8' },
    suspended:    { bg: '#F1F5F9', color: '#475569' },
  };
  const sc = statusColors[expert.verification_status] || statusColors.pending;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:999, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
      <div style={{ background:'white', borderRadius:'24px 24px 0 0', width:'100%', maxWidth:430, maxHeight:'92vh', overflowY:'auto' }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#0F2D5C,#1A4080)', padding:'20px 20px 24px', borderRadius:'24px 24px 0 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
            <div style={{ color:'white', fontWeight:900, fontSize:18 }}>Expert Profile</div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,.2)', border:'none', borderRadius:'50%', width:32, height:32, color:'white', cursor:'pointer', fontSize:16 }}>✕</button>
          </div>
          <div style={{ display:'flex', gap:14, alignItems:'center' }}>
            <div style={{ width:56, height:56, borderRadius:18, background:'rgba(255,255,255,.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, color:'white', fontWeight:900 }}>
              {(expert.name||'E')[0]}
            </div>
            <div>
              <div style={{ color:'white', fontWeight:900, fontSize:18 }}>{expert.name}</div>
              <div style={{ color:'rgba(255,255,255,.6)', fontSize:12, marginTop:2 }}>{expert.category} • {expert.city || expert.area}</div>
              <div style={{ display:'inline-block', background:sc.bg, color:sc.color, borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700, marginTop:6 }}>
                {expert.verification_status || 'pending'}
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding:'20px' }}>
          {/* Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:20 }}>
            {[
              { label:'Rating', value: expert.rating || '—' },
              { label:'Jobs Done', value: expert.jobs_done || 0 },
              { label:'Earnings', value: '₹' + (expert.earnings || 0).toLocaleString() },
            ].map(({ label, value }) => (
              <div key={label} style={{ background:'#F4F6FA', borderRadius:12, padding:'10px', textAlign:'center' }}>
                <div style={{ fontWeight:900, fontSize:16, color:'#0F2D5C' }}>{value}</div>
                <div style={{ fontSize:10, color:'#9A9A9A', marginTop:2 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Details */}
          <div style={{ background:'#F4F6FA', borderRadius:16, padding:14, marginBottom:16 }}>
            {[
              ['📱', 'Phone', '+91 ' + expert.phone],
              ['📧', 'Email', expert.email || '—'],
              ['📍', 'Area', expert.city || expert.area || '—'],
              ['🛠', 'Skills', expert.skills || expert.category || '—'],
              ['💼', 'Experience', expert.experience || '—'],
              ['🗣', 'Languages', expert.languages || '—'],
              ['🏦', 'Bank', expert.bank_name ? `${expert.bank_name} ••••${String(expert.bank_account||'').slice(-4)}` : '—'],
              ['🪪', 'PAN', expert.pan_number || '—'],
              ['📅', 'Registered', expert.registered_at ? new Date(expert.registered_at).toLocaleDateString('en-IN') : '—'],
            ].map(([icon, label, value]) => (
              <div key={label} style={{ display:'flex', gap:10, padding:'8px 0', borderBottom:'1px solid #E8E8E8' }}>
                <span style={{ fontSize:16, flexShrink:0, width:24, textAlign:'center' }}>{icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:10, color:'#9A9A9A' }}>{label}</div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#0D0D0D' }}>{value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Documents */}
          {(expert.selfie_url || expert.aadhaar_front_url || expert.pan_url) && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:13, color:'#545454', marginBottom:10 }}>📄 Documents</div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {[
                  { label:'Selfie', url: expert.selfie_url },
                  { label:'Aadhaar Front', url: expert.aadhaar_front_url },
                  { label:'Aadhaar Back', url: expert.aadhaar_back_url },
                  { label:'PAN Card', url: expert.pan_url },
                ].filter(d => d.url && d.url.startsWith('http')).map(doc => (
                  <a key={doc.label} href={doc.url} target="_blank" rel="noreferrer"
                    style={{ display:'block', background:'#F4F6FA', border:'1px solid #E8E8E8', borderRadius:10, padding:'8px 12px', fontSize:12, color:'#0F2D5C', fontWeight:600, textDecoration:'none' }}>
                    🖼 {doc.label}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Recent bookings */}
          {bookings.length > 0 && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:13, color:'#545454', marginBottom:10 }}>📋 Recent Jobs ({bookings.length})</div>
              {bookings.slice(0,5).map(b => (
                <div key={b.id} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #F4F6FA' }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#0D0D0D' }}>{b.service_name}</div>
                    <div style={{ fontSize:11, color:'#9A9A9A' }}>{b.area} • {b.date}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'#0F2D5C' }}>₹{b.amount}</div>
                    <Badge status={b.status}/>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Admin note */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#545454', marginBottom:6 }}>Admin Note</div>
            <textarea value={note || expert.admin_note || ''} onChange={e => setNote(e.target.value)}
              placeholder="Add a note (shown to expert)..."
              style={{ width:'100%', padding:'10px 14px', borderRadius:12, border:'1px solid #E8E8E8', fontSize:13, resize:'none', height:70, boxSizing:'border-box', outline:'none', fontFamily:'inherit' }}/>
          </div>

          {/* Action buttons */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <button onClick={() => updateStatus('approved')} disabled={updating}
              style={{ padding:'12px', background:'#ECFDF5', border:'1px solid #6EE7B7', borderRadius:12, color:'#065F46', fontWeight:700, fontSize:13, cursor:'pointer' }}>
              ✅ Approve
            </button>
            <button onClick={() => updateStatus('rejected')} disabled={updating}
              style={{ padding:'12px', background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:12, color:'#DC2626', fontWeight:700, fontSize:13, cursor:'pointer' }}>
              ❌ Reject
            </button>
            <button onClick={() => updateStatus('more_docs')} disabled={updating}
              style={{ padding:'12px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:12, color:'#1D4ED8', fontWeight:700, fontSize:13, cursor:'pointer' }}>
              📄 Need Docs
            </button>
            <button onClick={() => updateStatus('suspended')} disabled={updating}
              style={{ padding:'12px', background:'#F1F5F9', border:'1px solid #CBD5E1', borderRadius:12, color:'#475569', fontWeight:700, fontSize:13, cursor:'pointer' }}>
              🚫 Suspend
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── CUSTOMER DETAIL MODAL ─────────────────────────────────────────────────────
function CustomerDetailModal({ customer, allBookings, onClose }) {
  if (!customer) return null;
  const customerBookings = allBookings.filter(b => b.customer_phone === customer.phone);
  const totalSpent = customerBookings.reduce((s, b) => s + (b.amount || 0), 0);
  const isRepeat = customerBookings.length > 1;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:999, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
      <div style={{ background:'white', borderRadius:'24px 24px 0 0', width:'100%', maxWidth:430, maxHeight:'85vh', overflowY:'auto' }}>
        <div style={{ background:'linear-gradient(135deg,#0F2D5C,#1A4080)', padding:'20px', borderRadius:'24px 24px 0 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
            <div style={{ color:'white', fontWeight:900, fontSize:18 }}>Customer Profile</div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,.2)', border:'none', borderRadius:'50%', width:32, height:32, color:'white', cursor:'pointer', fontSize:16 }}>✕</button>
          </div>
          <div style={{ display:'flex', gap:14, alignItems:'center' }}>
            <div style={{ width:52, height:52, borderRadius:18, background:'rgba(255,255,255,.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, color:'white', fontWeight:900 }}>
              {(customer.name||'C')[0]}
            </div>
            <div>
              <div style={{ color:'white', fontWeight:900, fontSize:17 }}>{customer.name || 'Customer'}</div>
              <div style={{ color:'rgba(255,255,255,.6)', fontSize:12 }}>+91 {customer.phone}</div>
              {isRepeat && <div style={{ display:'inline-block', background:'#FBBF24', color:'#0D0D0D', borderRadius:20, padding:'2px 10px', fontSize:10, fontWeight:700, marginTop:4 }}>⭐ Repeat Customer</div>}
            </div>
          </div>
        </div>

        <div style={{ padding:'20px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:20 }}>
            {[
              { label:'Total Bookings', value: customerBookings.length },
              { label:'Total Spent', value: '₹' + totalSpent.toLocaleString() },
              { label:'Avg Order', value: customerBookings.length ? '₹' + Math.round(totalSpent / customerBookings.length) : '—' },
            ].map(({ label, value }) => (
              <div key={label} style={{ background:'#F4F6FA', borderRadius:12, padding:'10px', textAlign:'center' }}>
                <div style={{ fontWeight:900, fontSize:15, color:'#0F2D5C' }}>{value}</div>
                <div style={{ fontSize:10, color:'#9A9A9A', marginTop:2 }}>{label}</div>
              </div>
            ))}
          </div>

          <div style={{ fontWeight:700, fontSize:13, color:'#545454', marginBottom:10 }}>📋 Booking History</div>
          {customerBookings.length === 0
            ? <div style={{ textAlign:'center', color:'#9A9A9A', padding:20 }}>No bookings yet</div>
            : customerBookings.map(b => (
              <div key={b.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #F4F6FA' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:'#0D0D0D' }}>{b.service_name}</div>
                  <div style={{ fontSize:11, color:'#9A9A9A' }}>{b.area} • {b.date} • 👨‍🔧 {b.pro_name || 'Unassigned'}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#0F2D5C' }}>₹{b.amount}</div>
                  <Badge status={b.status}/>
                </div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}

// ── MAIN ADMIN APP ────────────────────────────────────────────────────────────
export default function AdminApp({ lang, setLang, onLogout, adminUser }) {
  const [tab, setTab]                   = useState('dashboard');
  const [bookings, setBookings]         = useState([]);
  const [experts, setExperts]           = useState([]);
  const [users, setUsers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [selectedExpert, setExpert]     = useState(null);
  const [selectedCustomer, setCustomer] = useState(null);
  const [expertFilter, setEFilter]      = useState('all');
  const [searchExpert, setSearchE]      = useState('');
  const [searchCustomer, setSearchC]    = useState('');
  const t = TRANSLATIONS[lang];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [{ data: bData, error: bErr }, { data: pData, error: pErr }, { data: uData }] = await Promise.all([
          supabase.from('bookings').select('*').order('created_at', { ascending: false }).limit(200),
          supabase.from('professionals').select('*').order('registered_at', { ascending: false }),
          supabase.from('users').select('*').order('created_at', { ascending: false }),
        ]);
        if (bErr) throw bErr;
        if (pErr) throw pErr;
        setBookings(bData || []);
        setExperts(pData || []);
        setUsers(uData || []);
      } catch (e) {
        console.error('AdminApp load error:', e);
        setError('Failed to load data. Please refresh.');
      }
      setLoading(false);
    };
    load();
  }, []);

  // ── Derived stats ────────────────────────────────────────────────────────
  const totalRevenue    = bookings.reduce((s, b) => s + (b.amount || 0), 0);
  const platformCut     = Math.round(totalRevenue * (PLATFORM_FEE_PCT / 100));
  const pendingExperts  = experts.filter(e => e.verification_status === 'pending' || e.verification_status === 'under_review').length;
  const approvedExperts = experts.filter(e => e.verification_status === 'approved').length;

  // Repeat customers = phone appears in bookings more than once
  const phoneCounts = bookings.reduce((acc, b) => {
    acc[b.customer_phone] = (acc[b.customer_phone] || 0) + 1;
    return acc;
  }, {});
  const repeatCustomers = Object.values(phoneCounts).filter(c => c > 1).length;
  const uniqueCustomers = Object.keys(phoneCounts).length;

  // New experts this week
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const newExpertsThisWeek = experts.filter(e => e.registered_at && new Date(e.registered_at) > weekAgo).length;

  // New customers this week
  const newCustomersThisWeek = users.filter(u => u.created_at && new Date(u.created_at) > weekAgo && u.role === 'customer').length;

  // Unique customers list with booking counts
  const customerList = Object.entries(phoneCounts).map(([phone, count]) => {
    const booking = bookings.find(b => b.customer_phone === phone);
    const user = users.find(u => u.phone === phone);
    return {
      phone,
      name: user?.name || booking?.customer_name || 'Customer',
      bookingCount: count,
      totalSpent: bookings.filter(b => b.customer_phone === phone).reduce((s, b) => s + (b.amount || 0), 0),
      lastBooking: bookings.find(b => b.customer_phone === phone),
    };
  }).sort((a, b) => b.bookingCount - a.bookingCount);

  const updateExpertStatus = (phone, status, note) => {
    setExperts(prev => prev.map(e => e.phone === phone ? { ...e, verification_status: status, admin_note: note } : e));
  };

  // Filter experts
  const filteredExperts = experts.filter(e => {
    const matchFilter = expertFilter === 'all' || e.verification_status === expertFilter;
    const matchSearch = !searchExpert || e.name?.toLowerCase().includes(searchExpert.toLowerCase()) || e.phone?.includes(searchExpert);
    return matchFilter && matchSearch;
  });

  // Filter customers
  const filteredCustomers = customerList.filter(c =>
    !searchCustomer || c.name.toLowerCase().includes(searchCustomer.toLowerCase()) || c.phone.includes(searchCustomer)
  );

  if (loading) return <LoadingSpinner fullScreen message="Loading admin data..."/>;

  if (error) return (
    <div style={{ padding:24, textAlign:'center' }}>
      <p style={{ color:'#DC2626', marginBottom:16 }}>{error}</p>
      <button onClick={() => window.location.reload()}
        style={{ padding:'10px 24px', background:'#0F2D5C', color:'white', border:'none', borderRadius:12, fontWeight:700, cursor:'pointer' }}>
        Refresh
      </button>
    </div>
  );

  return (
    <>
      {/* ── DASHBOARD ── */}
      {tab === 'dashboard' && (
        <div style={{ paddingBottom:90, background:'#F4F6FA', minHeight:'100vh' }}>
          {/* Header */}
          <div style={{ background:`linear-gradient(135deg,#0F2D5C,#1A4080)`, padding:'52px 16px 24px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <GharPeLogoWhite size={32}/>
              <div style={{ display:'flex', gap:8 }}>
                <LangToggle lang={lang} setLang={setLang}/>
                <button onClick={onLogout}
                  style={{ background:'rgba(255,255,255,.15)', border:'1px solid rgba(255,255,255,.25)', borderRadius:20, padding:'5px 12px', color:'white', fontWeight:600, fontSize:11, cursor:'pointer' }}>
                  Sign out
                </button>
              </div>
            </div>
            <div style={{ color:'rgba(255,255,255,.6)', fontSize:12 }}>Admin Dashboard • Lucknow</div>
            {adminUser?.name && <div style={{ color:'white', fontWeight:700, fontSize:14, marginTop:2 }}>Welcome, {adminUser.name} 👋</div>}
          </div>

          <div style={{ padding:'16px' }}>
            {/* Pending alert */}
            {pendingExperts > 0 && (
              <div onClick={() => setTab('experts')}
                style={{ background:'linear-gradient(135deg,#FBBF24,#F59E0B)', borderRadius:16, padding:'14px 18px', marginBottom:16, cursor:'pointer', display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ fontSize:24 }}>⚠️</span>
                <div>
                  <div style={{ fontWeight:800, color:'#0D0D0D', fontSize:14 }}>{pendingExperts} expert{pendingExperts > 1 ? 's' : ''} awaiting review</div>
                  <div style={{ fontSize:12, color:'#78350F', marginTop:2 }}>Tap to review →</div>
                </div>
              </div>
            )}

            {/* Main stats */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              {[
                { label:'Total Revenue', value:'₹' + totalRevenue.toLocaleString(), color:'#1D4ED8', bg:'#EFF6FF', icon:'💰' },
                { label:`Platform (${PLATFORM_FEE_PCT}%)`, value:'₹' + platformCut.toLocaleString(), color:'#065F46', bg:'#ECFDF5', icon:'🏢' },
                { label:'Total Bookings', value: bookings.length, color:'#A16207', bg:'#FEF9C3', icon:'📋' },
                { label:'Active Experts', value: approvedExperts, color:'#6D28D9', bg:'#F3E8FF', icon:'👨‍🔧' },
              ].map(({ label, value, color, bg, icon }) => (
                <div key={label} style={{ background:bg, borderRadius:14, padding:14, border:`1px solid ${color}22` }}>
                  <div style={{ fontSize:11, color:'#9A9A9A' }}>{icon} {label}</div>
                  <div style={{ fontWeight:900, fontSize:22, color, marginTop:4 }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Customer & Expert stats */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:16 }}>
              {[
                { label:'Unique Customers', value: uniqueCustomers, color:'#0F2D5C', bg:'#E8EEF8' },
                { label:'Repeat Customers', value: repeatCustomers, color:'#065F46', bg:'#ECFDF5' },
                { label:'New This Week', value: newCustomersThisWeek, color:'#DC2626', bg:'#FEE2E2' },
              ].map(({ label, value, color, bg }) => (
                <div key={label} style={{ background:bg, borderRadius:12, padding:'10px 12px' }}>
                  <div style={{ fontWeight:900, fontSize:18, color }}>{value}</div>
                  <div style={{ fontSize:10, color:'#9A9A9A', marginTop:2 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Recent bookings */}
            <div style={{ fontWeight:700, fontSize:15, color:'#0D0D0D', marginBottom:12 }}>📋 Recent Bookings</div>
            {bookings.slice(0, 8).map(b => (
              <div key={b.id} style={{ background:'white', borderRadius:14, padding:14, marginBottom:10, boxShadow:'0 2px 6px rgba(0,0,0,.05)', border:'1px solid #E8E8E8' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                  <span style={{ fontWeight:700, color:'#0D0D0D', fontSize:14 }}>{b.service_name}</span>
                  <Badge status={b.status}/>
                </div>
                <div style={{ fontSize:12, color:'#9A9A9A', marginBottom:4 }}>
                  👤 {b.customer_name || b.customer_phone} • 👨‍🔧 {b.pro_name || 'Unassigned'}
                </div>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, color:'#9A9A9A' }}>#{String(b.id).slice(0,8).toUpperCase()} • {b.area}</span>
                  <span style={{ fontWeight:700, color:'#0F2D5C', fontSize:13 }}>₹{b.amount}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── EXPERTS TAB ── */}
      {tab === 'experts' && (
        <div style={{ paddingBottom:90, background:'#F4F6FA', minHeight:'100vh' }}>
          <div style={{ background:`linear-gradient(135deg,#0F2D5C,#1A4080)`, padding:'52px 16px 20px' }}>
            <div style={{ color:'white', fontWeight:900, fontSize:20, marginBottom:4 }}>👨‍🔧 Experts</div>
            <div style={{ color:'rgba(255,255,255,.6)', fontSize:12 }}>{experts.length} total • {newExpertsThisWeek} new this week</div>
          </div>

          <div style={{ padding:'16px' }}>
            {/* Search */}
            <input value={searchExpert} onChange={e => setSearchE(e.target.value)}
              placeholder="🔍 Search by name or phone..."
              style={{ width:'100%', padding:'12px 16px', borderRadius:14, border:'1px solid #E8E8E8', fontSize:14, boxSizing:'border-box', outline:'none', marginBottom:12, background:'white' }}/>

            {/* Filter tabs */}
            <div style={{ display:'flex', gap:6, marginBottom:16, overflowX:'auto', paddingBottom:4 }}>
              {[
                { id:'all', label:`All (${experts.length})` },
                { id:'pending', label:`⏳ Pending (${experts.filter(e=>e.verification_status==='pending').length})` },
                { id:'under_review', label:`🔍 Review (${experts.filter(e=>e.verification_status==='under_review').length})` },
                { id:'approved', label:`✅ Approved (${approvedExperts})` },
                { id:'rejected', label:`❌ Rejected (${experts.filter(e=>e.verification_status==='rejected').length})` },
              ].map(f => (
                <button key={f.id} onClick={() => setEFilter(f.id)}
                  style={{ padding:'7px 14px', background:expertFilter===f.id?'#0F2D5C':'white', border:`1px solid ${expertFilter===f.id?'#0F2D5C':'#E8E8E8'}`, borderRadius:20, color:expertFilter===f.id?'white':'#545454', fontWeight:600, fontSize:12, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
                  {f.label}
                </button>
              ))}
            </div>

            {filteredExperts.length === 0
              ? <EmptyState icon="👨‍🔧" title="No experts found" subtitle="Try changing your filter or search term"/>
              : filteredExperts.map(e => (
                <div key={e.id || e.phone} onClick={() => setExpert(e)}
                  style={{ background:'white', borderRadius:16, padding:16, marginBottom:12, boxShadow:'0 2px 8px rgba(0,0,0,.06)', border:'1px solid #E8E8E8', cursor:'pointer' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                    <div style={{ display:'flex', gap:12, alignItems:'center', flex:1, minWidth:0 }}>
                      <div style={{ width:44, height:44, borderRadius:14, background:'#E8EEF8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:900, color:'#0F2D5C', flexShrink:0 }}>
                        {(e.name||'E')[0]}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, color:'#0D0D0D', fontSize:15 }}>{e.name}</div>
                        <div style={{ fontSize:12, color:'#9A9A9A' }}>{e.category} • {e.city || e.area}</div>
                        <div style={{ fontSize:11, color:'#9A9A9A' }}>📱 {e.phone}</div>
                      </div>
                    </div>
                    <Badge status={e.verification_status || 'pending'}/>
                  </div>
                  <div style={{ display:'flex', gap:16, paddingTop:8, borderTop:'1px solid #F4F6FA' }}>
                    <span style={{ fontSize:12, color:'#545454' }}>⭐ {e.rating || '—'}</span>
                    <span style={{ fontSize:12, color:'#545454' }}>✅ {e.jobs_done || 0} jobs</span>
                    <span style={{ fontSize:12, color:'#065F46', fontWeight:600 }}>₹{(e.earnings||0).toLocaleString()}</span>
                    <span style={{ fontSize:11, color:'#9A9A9A', marginLeft:'auto' }}>
                      {e.registered_at ? new Date(e.registered_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short'}) : '—'}
                    </span>
                  </div>
                  <div style={{ fontSize:11, color:'#9A9A9A', marginTop:4, textAlign:'right' }}>Tap to view details →</div>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* ── CUSTOMERS TAB ── */}
      {tab === 'customers' && (
        <div style={{ paddingBottom:90, background:'#F4F6FA', minHeight:'100vh' }}>
          <div style={{ background:`linear-gradient(135deg,#0F2D5C,#1A4080)`, padding:'52px 16px 20px' }}>
            <div style={{ color:'white', fontWeight:900, fontSize:20, marginBottom:4 }}>👥 Customers</div>
            <div style={{ color:'rgba(255,255,255,.6)', fontSize:12 }}>
              {uniqueCustomers} unique • {repeatCustomers} repeat • {newCustomersThisWeek} new this week
            </div>
          </div>

          <div style={{ padding:'16px' }}>
            <input value={searchCustomer} onChange={e => setSearchC(e.target.value)}
              placeholder="🔍 Search by name or phone..."
              style={{ width:'100%', padding:'12px 16px', borderRadius:14, border:'1px solid #E8E8E8', fontSize:14, boxSizing:'border-box', outline:'none', marginBottom:16, background:'white' }}/>

            {/* Stats row */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:16 }}>
              {[
                { label:'Total Customers', value: uniqueCustomers, color:'#0F2D5C', bg:'#E8EEF8' },
                { label:'Repeat', value: repeatCustomers, color:'#065F46', bg:'#ECFDF5' },
                { label:'New This Week', value: newCustomersThisWeek, color:'#DC2626', bg:'#FEE2E2' },
              ].map(({ label, value, color, bg }) => (
                <div key={label} style={{ background:bg, borderRadius:12, padding:'10px 12px' }}>
                  <div style={{ fontWeight:900, fontSize:18, color }}>{value}</div>
                  <div style={{ fontSize:10, color:'#9A9A9A', marginTop:2 }}>{label}</div>
                </div>
              ))}
            </div>

            {filteredCustomers.length === 0
              ? <EmptyState icon="👥" title="No customers yet" subtitle="Customers will appear here after their first booking"/>
              : filteredCustomers.map(c => (
                <div key={c.phone} onClick={() => setCustomer(c)}
                  style={{ background:'white', borderRadius:16, padding:16, marginBottom:12, boxShadow:'0 2px 8px rgba(0,0,0,.06)', border:'1px solid #E8E8E8', cursor:'pointer' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                      <div style={{ width:44, height:44, borderRadius:14, background:'#E8EEF8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:900, color:'#0F2D5C' }}>
                        {(c.name||'C')[0]}
                      </div>
                      <div>
                        <div style={{ fontWeight:700, color:'#0D0D0D', fontSize:15 }}>{c.name}</div>
                        <div style={{ fontSize:12, color:'#9A9A9A' }}>📱 {c.phone}</div>
                        <div style={{ fontSize:12, color:'#9A9A9A' }}>
                          {c.bookingCount} booking{c.bookingCount>1?'s':''} • ₹{c.totalSpent.toLocaleString()} spent
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      {c.bookingCount > 1 && (
                        <div style={{ background:'#FEF9C3', color:'#A16207', borderRadius:20, padding:'2px 8px', fontSize:10, fontWeight:700, marginBottom:4 }}>⭐ Repeat</div>
                      )}
                      <div style={{ fontSize:11, color:'#9A9A9A' }}>Tap to view →</div>
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* ── SERVICES TAB ── */}
      {tab === 'services' && (
        <div style={{ paddingBottom:90, background:'#F4F6FA', minHeight:'100vh' }}>
          <div style={{ background:`linear-gradient(135deg,#0F2D5C,#1A4080)`, padding:'52px 16px 20px' }}>
            <div style={{ color:'white', fontWeight:900, fontSize:20 }}>🛠 Manage Services</div>
          </div>
          <div style={{ padding:'16px' }}>
            {SERVICES.map(s => (
              <div key={s.id} style={{ background:s.color, borderRadius:14, padding:16, marginBottom:12, border:'1px solid rgba(0,0,0,.05)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ fontSize:24 }}>{s.icon}</span>
                    <div>
                      <div style={{ fontWeight:700, color:'#0D0D0D' }}>{lang==='en'?s.name:s.nameHi}</div>
                      <div style={{ fontSize:12, color:'#9A9A9A' }}>{s.services.length} services</div>
                    </div>
                  </div>
                  <button style={{ padding:'6px 14px', background:'#0F2D5C', color:'white', border:'none', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedExpert && (
        <ExpertDetailModal expert={selectedExpert} onClose={() => setExpert(null)} onStatusUpdate={updateExpertStatus}/>
      )}
      {selectedCustomer && (
        <CustomerDetailModal customer={selectedCustomer} allBookings={bookings} onClose={() => setCustomer(null)}/>
      )}

      <BottomNav active={tab} onSelect={setTab} tabs={[
        { id:'dashboard', icon:'📊', label:'Dashboard' },
        { id:'experts',   icon:'👨‍🔧', label:'Experts' },
        { id:'customers', icon:'👥', label:'Customers' },
        { id:'services',  icon:'🛠', label:'Services' },
      ]}/>
    </>
  );
}
